import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search as MagnifyingGlass, MapPin, Briefcase, Crosshair, Star, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocation as useUserLocation } from "@/context/LocationContext";
import { loginRedirect } from "@/components/Layout";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";

const CATS = [
  { key: "", label: "All" },
  { key: "service", label: "Service pros" },
  { key: "business_owner", label: "Business owners" },
  { key: "job", label: "Job seekers" },
  { key: "doctor", label: "Doctors" },
  { key: "freelancer", label: "Freelancers" },
  { key: "student", label: "Students" },
];

export default function Citizens() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { loc, detect, loading: locLoading } = useUserLocation();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/users/directory", { params: { q, profession_category: cat || undefined, lat: loc.lat, lng: loc.lng, radius_km: 50 } })
      .then(r => setUsers(r.data))
      .finally(() => setLoading(false));
  }, [q, cat, loc.lat, loc.lng]);

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">People near you</div>
          <h1 className="heading text-3xl md:text-4xl font-extrabold text-stone-900">Citizens directory</h1>
          <p className="text-stone-600 mt-1">Discover neighbourhood workers, business owners, doctors & freelancers.</p>
          <button onClick={() => detect()} className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-[#E05E36]" data-testid="citizens-locate">
            <Crosshair size={13} className={locLoading ? "animate-spin" : ""} /> Near {loc.city}
          </button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 flex-1 min-w-[240px] bg-white rounded-xl border border-stone-200 px-3 py-2">
          <MagnifyingGlass size={16} className="text-stone-500" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name, skill, business…" className="bg-transparent w-full outline-none text-sm" data-testid="citizens-search" />
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {CATS.map(c => (
            <button key={c.key} onClick={() => setCat(c.key)} className={`text-xs font-semibold px-3 py-2 rounded-full border whitespace-nowrap ${cat === c.key ? "bg-[#E05E36] text-white border-[#E05E36]" : "bg-white border-stone-200 hover:border-stone-300"}`} data-testid={`citizens-cat-${c.key || "all"}`}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 text-sm text-stone-500">{loading ? "Searching…" : <><b>{users.length}</b> profiles</>}</div>

      <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {users.map((u, i) => (
          <motion.div key={u.user_id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }} className="bg-white border border-stone-200 rounded-2xl p-5 hover:-translate-y-1 hover:shadow-md transition-transform" data-testid={`citizen-${u.user_id}`}>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-[#E05E36] text-white grid place-items-center font-bold text-lg overflow-hidden shrink-0">
                {u.picture ? <img loading="lazy" src={u.picture} alt="" className="w-full h-full object-cover" /> : (u.name?.[0]?.toUpperCase() || "U")}
              </div>
              <div className="min-w-0 flex-1">
                <div className="heading font-bold text-stone-900 truncate">{u.name}</div>
                {u.occupation && <div className="text-sm text-stone-600 truncate">{u.occupation}</div>}
              </div>
              <button
                onClick={async (e) => {
                  e.preventDefault(); e.stopPropagation();
                  if (!user) { loginRedirect(); return; }
                  try {
                    const r = await api.post("/conversations/start", { other_user_id: u.user_id });
                    navigate(`/messages/${r.data.conv_id}`);
                  } catch (er) { toast.error(er?.response?.data?.detail || "Could not start chat"); }
                }}
                className="w-9 h-9 rounded-full bg-[#2D5A27] hover:bg-[#24491f] text-white grid place-items-center shrink-0"
                title="Message"
                data-testid={`msg-${u.user_id}`}
              >
                <MessageCircle size={16} />
              </button>
            </div>
            {u.business_name && (
              <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold bg-stone-50 text-stone-700 px-2.5 py-1 rounded-full">
                <Briefcase size={12} /> {u.business_name}
              </div>
            )}
            {u.bio && <div className="mt-3 text-sm text-stone-600 line-clamp-2">{u.bio}</div>}
            {u.skills?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {u.skills.slice(0, 4).map(s => <span key={s} className="text-[10px] font-semibold px-2 py-0.5 bg-stone-100 rounded-full">#{s}</span>)}
              </div>
            )}
            <div className="mt-3 flex items-center gap-3 text-xs text-stone-500 pt-3 border-t border-stone-100">
              {u.city && <span className="flex items-center gap-1"><MapPin size={12} /> {u.city}{u.distance_km !== undefined && u.distance_km !== 9999 && ` · ${u.distance_km} km`}</span>}
              {u.experience_years ? <span>{u.experience_years} yrs</span> : null}
            </div>
          </motion.div>
        ))}
        {!loading && users.length === 0 && (
          <div className="col-span-full text-center py-12 text-stone-500">
            No profiles yet. <Link to="/account" className="text-[#E05E36] font-semibold">Complete yours →</Link>
          </div>
        )}
      </div>
    </div>
  );
}
